/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC } from "react";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { InternalRoutes } from "./InternalRoutes";
import { MerchantRoutes } from "./MerchantRoutes";
import { ErrorsPage } from "../modules/errors/ErrorsPage";
import { App } from "../App";
import { AuthPage, Logout, useAuth } from "../modules/auth";

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;

const AppRoutes: FC = () => {
  const { currentUser } = useAuth();

  // Determine user role and redirect accordingly
  const getUserRole = () => {
    if (!currentUser?.auth?.role) return null;
    return currentUser.auth.role;
  };

  const userRole = getUserRole();

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path="error/*" element={<ErrorsPage />} />
          <Route path="logout" element={<Logout />} />
          {currentUser ? (
            <>
              {userRole?.includes("INTERNAL") ? (
                <>
                  <Route path="internal/*" element={<InternalRoutes />} />
                  <Route
                    index
                    element={<Navigate to="/internal/transaction-history" />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/internal/transaction-history" />}
                  />
                </>
              ) : userRole?.includes("MERCHANT") ? (
                <>
                  <Route path="merchant/*" element={<MerchantRoutes />} />
                  <Route
                    index
                    element={<Navigate to="/merchant/transaction-history" />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/merchant/transaction-history" />}
                  />
                </>
              ) : (
                <>
                  <Route path="*" element={<Navigate to="/auth" />} />
                </>
              )}
            </>
          ) : (
            <>
              <Route path="auth/*" element={<AuthPage />} />
              <Route path="*" element={<Navigate to="/auth" />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export { AppRoutes };
